import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import { SortableContext, arrayMove } from "@dnd-kit/sortable";
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  ReactElement,
} from "react";

import { useAppRouteId, useAppRouteParams } from "../../../../AppRoutes";
import {
  Sheet,
  SheetOptimizeTypesGroup,
  sheetOptimizeTypeGroupList,
  sheetOptimizeTypeMap,
  useUpdateSheet,
} from "../../../../domain/Sheet";
import { ManagedTransaction } from "../../../../firebase/firestore";
import { useAppAccount } from "../../../AppProvider/AppAccountProvider";
import { useAppNotificationManager } from "../../../AppProvider/AppNotificationProvider";
import { useDialogContextMenuItemsSheet } from "../../../Dialog/DialogFirestore/DialogSheet/hooks";
import { SortableTmpMenuSet } from "../../helpers";

import styles from "./index.module.css";

import { useTadSheetsWithPermission } from "@/components/GridTable/GridTableTadSheets/hooks";
import { usePermissions } from "@/components/InOrganizationProvider/PermissionsProvider";
import { LoadingCoverWithoutText } from "@/components/LoadingCover";
import { TadSheet } from "@/domain/TadSheet";
import { useAuthorizedSheets } from "@/hooks/useAuthorizedCollections";
import { useVersionPermission } from "@/hooks/useCollectionPermission";
import { useReferencedSheets } from "@/hooks/useReferencedSheets";
import { translation } from "@/i18n";
import { nsSideNav } from "@/i18n/locales/sideNav";

const useTranslation = translation.use(nsSideNav);

/**
 * @description シート一覧を表示し、編集や新規作成の操作機能を提供
 */
export const SheetsMenu: React.FC = () => {
  const { t } = useTranslation();
  const { versionId } = useAppRouteParams();
  const { hasPermission, isLoadedMe } = usePermissions();
  const hasVersionsMetaWritePermission = hasPermission(
    "versions_meta",
    "write"
  );
  const isSortDisabled = !hasVersionsMetaWritePermission;
  const { isAAMemberOrAbove } = useAppAccount();
  const [sheets, isLoadedSheets] = useAuthorizedSheets(versionId);
  const [referencedSheets, isLoadedReferencedSheets] = useReferencedSheets();
  // 括弧書きの参照先名。TADS 単位の read しか無い人にも、その TADS の表示名を出す
  const { tadSheets } = useTadSheetsWithPermission({ includeParametric: true });

  const [isCloseSheets, setIsCloseSheets] = useState(false);

  const sortedSheets = useMemo(
    () =>
      [...sheets].sort((a, b) => {
        return Math.sign(a.sort - b.sort);
      }),
    [sheets]
  );

  const {
    renderDialogsSheet,
    getContextMenuItemsSheet,
    getContextMenuItemsSheetsMenu,
  } = useDialogContextMenuItemsSheet(sortedSheets);

  const contextMenuItemsSheets: ReactElement = getContextMenuItemsSheetsMenu();

  // 非表示設定でないシート
  const visibleSheets = useMemo(() => {
    return sortedSheets.filter((sheet) => {
      if (sheet.isHidden) return false;
      const { referTo } = sheet;
      if (!referTo) return true;
      const refSheet = referencedSheets.find(
        (refSheet) =>
          refSheet.id === referTo.sheetId &&
          refSheet.refVersionId === referTo.versionId
      );
      return !refSheet?.isHidden;
    });
  }, [sortedSheets, referencedSheets]);

  // 実際に描画されるシート
  // NOTE: 非表示のシートはAAメンバーであれば見れる
  const displaySheets = useMemo(() => {
    return isAAMemberOrAbove ? sortedSheets : visibleSheets;
  }, [sortedSheets, visibleSheets, isAAMemberOrAbove]);

  // 実際に描画されるシートカテゴリとシート配列のmap
  const displaySheetsMap = useMemo(() => {
    const result = new Map<SheetOptimizeTypesGroup, Sheet[]>();
    for (const sheetOptimizeTypeGroup of sheetOptimizeTypeGroupList) {
      const targetOptimizeTypes = sheetOptimizeTypeMap.get(
        sheetOptimizeTypeGroup
      );
      if (!targetOptimizeTypes) continue;

      const matchTypeSheets = displaySheets.filter((sheet) => {
        // シートタイプの条件を満たす
        const matchesType = targetOptimizeTypes.includes(
          sheet.sheetOptimizeType
        );

        return matchesType;
      });

      result.set(sheetOptimizeTypeGroup, matchTypeSheets);
    }
    return result;
  }, [displaySheets]);

  // シートが存在するが、それらが全て非表示の場合はシートメニューにロックアイコンを表示する
  const allHidden = sortedSheets.length > 0 && visibleSheets.length === 0;

  if (!isLoadedSheets || !isLoadedReferencedSheets || !isLoadedMe) {
    return <LoadingCoverWithoutText />;
  }

  // シートが空でAAメンバーでない場合は何も表示しない
  if (visibleSheets.length === 0 && !isAAMemberOrAbove) {
    return null;
  }

  return (
    <>
      <SortableTmpMenuSet
        id={"sheet"}
        text={t.sideNav.item.sheets()}
        iconName={isCloseSheets ? "ChevronDown" : "ChevronUp"}
        onClick={() => {
          setIsCloseSheets(!isCloseSheets);
        }}
        contextMenuItems={contextMenuItemsSheets}
        isHidden={allHidden}
      />
      {!isCloseSheets &&
        Array.from(displaySheetsMap).map(([sheetOptimizeTypeGroup, sheets]) => {
          return (
            <SheetsSortableTmpMenuSet
              key={sheetOptimizeTypeGroup}
              sheetOptimizeTypeGroup={sheetOptimizeTypeGroup}
              sheets={sheets}
              referencedSheets={referencedSheets}
              tadSheets={tadSheets}
              getContextMenuItemsSheet={getContextMenuItemsSheet}
              isSortDisabled={isSortDisabled}
            />
          );
        })}
      {renderDialogsSheet(displaySheets)}
    </>
  );
};

const SheetsSortableTmpMenuSet: React.FC<{
  sheetOptimizeTypeGroup: SheetOptimizeTypesGroup;
  sheets: Sheet[];
  referencedSheets: (Sheet & { refVersionId: string })[];
  tadSheets: TadSheet[];
  getContextMenuItemsSheet: ReturnType<
    typeof useDialogContextMenuItemsSheet
  >["getContextMenuItemsSheet"];
  isSortDisabled?: boolean;
}> = ({
  sheetOptimizeTypeGroup,
  sheets,
  referencedSheets,
  tadSheets,
  getContextMenuItemsSheet,
  isSortDisabled,
}) => {
  const { t } = useTranslation();
  const { organizationId, versionId, sheetId } = useAppRouteParams();
  const appRouteId = useAppRouteId();
  const updateSheet = useUpdateSheet();

  /**
   * dnd-kit
   */
  const [displaySheets, setDisplaySheets] = useState(sheets);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor)
  );
  const { showErrorNotification } = useAppNotificationManager();
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;
      if (active.id !== over.id) {
        const oldIndex = sheets.findIndex((item) => item.id === active.id);
        const newIndex = sheets.findIndex((item) => item.id === over.id);
        const newSheets = arrayMove(sheets, oldIndex, newIndex);
        const newSheetSorts = newSheets
          .map(({ sort }) => sort)
          .sort((a, b) => Math.sign(a - b));
        const newSheetsWithSort = newSheets.map((sheet, index) => ({
          ...sheet,
          sort: newSheetSorts[index],
        }));
        setDisplaySheets(newSheetsWithSort);
        try {
          await ManagedTransaction.runTransaction(async (transaction) => {
            // NOTE: 既存のドキュメント更新のため、updateDocでOK
            for (const sheet of newSheetsWithSort) {
              await updateSheet(
                {
                  ...sheet,
                },
                { versionId, sheetId: sheet.id },
                { transaction }
              );
            }
          });
        } catch (error) {
          setDisplaySheets(sheets);
          showErrorNotification(t.sideNav.sortError.sheet(), error);
        }
      }
    },
    [t, sheets, updateSheet, versionId, showErrorNotification]
  );

  useEffect(() => {
    setDisplaySheets(sheets);
  }, [sheets]);

  return (
    <div key={sheetOptimizeTypeGroup}>
      <div className={styles.sheetOptimizeType}>{sheetOptimizeTypeGroup}</div>
      <div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={async (e) => {
            await handleDragEnd(e);
          }}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={displaySheets.map(({ id }) => id)}
            disabled={isSortDisabled}
          >
            {displaySheets.map((sheet) => {
              const refSheet = referencedSheets.find(
                (refSheet) =>
                  refSheet.id === sheet.referTo?.sheetId &&
                  refSheet.refVersionId === sheet.referTo?.versionId
              );
              const referencedSheetVersionId = refSheet?.refVersionId;
              const referencedTadSheet = tadSheets.find(
                (tadSheet) => tadSheet.id === sheet.tadReferTo?.tadSheetId
              );
              return (
                <SortableTmpMenuSet
                  key={sheet.id}
                  id={sheet.id}
                  text={sheet.displayName}
                  referencedSheetVersionId={referencedSheetVersionId}
                  referencedDisplayName={
                    sheet.tadReferTo
                      ? referencedTadSheet?.displayName ??
                        sheet.tadReferTo.tadSheetId
                      : undefined
                  }
                  iconName={
                    referencedSheetVersionId || sheet.tadReferTo
                      ? "ArrowUpRight"
                      : sheet.backend === "lds"
                        ? "CloudSQL"
                        : "Firestore"
                  }
                  isHidden={
                    refSheet
                      ? refSheet.isHidden || sheet.isHidden
                      : sheet.isHidden
                  }
                  to={`/organizations/${organizationId}/versions/${versionId}/sheets/${sheet.id}`}
                  active={appRouteId === "sheet" && sheetId === sheet.id}
                  contextMenuItems={getContextMenuItemsSheet(sheet)}
                  isSortable={!isSortDisabled}
                />
              );
            })}
          </SortableContext>
        </DndContext>
      </div>
    </div>
  );
};
